import { useWorldMapLocalStorage } from 'libs/hooks/ownership/useWorldMapLocalStorage';
import { EToastType } from 'libs/types/site/IClient';
import {
    EUploadResolution,
    isWithinSize,
    prepareImage
} from 'libs/utils/logic/operations/fileOperations';
import { userMessage } from 'libs/utils/logic/ui/uiLogic';
import { useEffect, useRef, useState } from 'react';
import { apiUploadFile } from 'static/api/directApi';

export interface IUploadedImage {
    data: {
        imageUrls: {
            [key in EUploadResolution]: string;
        };
    };
}

export const useUploadImage = (
    resolutions: EUploadResolution[],
    worldMapId: string,
    disableImageRefresh?: boolean
) => {
    const [files, setFiles] = useState<File[]>(null);
    const worldMapLocalStorage = useWorldMapLocalStorage();
    const [inProgress, setInProgress] = useState<number>(0);
    const refProgress = useRef<number>(0);
    const [uploaded, setUploaded] = useState<IUploadedImage>();
    const mountedRef = useRef<boolean>();
    const temporaryToken = worldMapLocalStorage.getTokenForMapId(worldMapId);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (files) {
            refProgress.current += files.length;
            setInProgress(refProgress.current);
            files.forEach((file) => {
                if (!isWithinSize(file.size, 25)) {
                    userMessage(EToastType.ERROR, 'Localization.ERROR_FILE_LARGE');
                    setInProgress(refProgress.current - 1);
                    refProgress.current -= 1;
                } else {
                    const formData = prepareImage(
                        file,
                        resolutions,
                        disableImageRefresh,
                        temporaryToken
                    );
                    apiUploadFile(formData)
                        .then((r) => {
                            if (mountedRef.current) {
                                setUploaded(r as unknown as IUploadedImage);
                                setInProgress(refProgress.current - 1);
                                refProgress.current -= 1;
                            }
                        })
                        .catch(() => {
                            userMessage(EToastType.ERROR, 'Localization.UPLOAD_FAILED');
                            setInProgress(refProgress.current - 1);
                            refProgress.current -= 1;
                        });
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [files]);

    return { inProgress, uploaded, setFiles };
};
